import { PanelCollapseIcon, PanelExpandIcon, Tooltip, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useReportMenuExpanded } from './useReportMenuExpanded';
import type { ReportMenuItem } from './types';

const EXPANDED_WIDTH = '232px';
const ICON_SIZE = '20px';
// The nav icons are stroked on a 2.5-unit margin inside their 20-unit viewBox, so with a stroke width of 1.75 their
// visible edge sits this far inside the box. The logo artwork is full-bleed, so it has to reproduce the inset to
// line up.
const ICON_GLYPH_INSET = `calc(${ICON_SIZE} * 1.625 / 20)`;
const ITEM_HEIGHT = '40px';
const ITEM_PADDING = '10px';
const WORDMARK_WIDTH = '128px';
// The wordmark's lettering is centred on y 10.77 of its 16-unit viewBox, not on the box centre of 8: the mark that
// stands in for the "o" reaches the top edge, while the letters sit on a baseline near the bottom. Centring the
// image box therefore leaves the lettering low next to the toggle, so lift it by that 2.77-unit difference.
const WORDMARK_OPTICAL_LIFT = `calc(${WORDMARK_WIDTH} * -2.77 / 126)`;
const LOGO_MARK_WIDTH = '32px';
// Keeps every icon on one vertical line: collapsed, the nav is exactly one item wide plus its own padding.
const NAV_PADDING = theme.spacing.d;
const COLLAPSED_WIDTH = `calc(${ITEM_HEIGHT} + 2 * ${NAV_PADDING})`;
// Sets the branding block apart from the navigation items below it.
const HEADER_GAP = theme.spacing.g;
// Long enough that the labels stay quiet while the pointer is only passing over the nav on its way elsewhere.
const TOOLTIP_DELAY_MS = 500;
// Selected and hovered rows share this one wash against the white nav. It is the lightest step the palette offers,
// so there is no room for hover to sit above it without out-shouting the selection: the selected row is told apart
// by its bold label and primary-coloured icon instead of a darker fill.
const ROW_HIGHLIGHT = theme.colors.backgroundBasic.default;

type ReportMenuProps = Readonly<{
  items: ReportMenuItem[];
  /** Keeps the current query string (report filters) when navigating between menu items. */
  preserveQueryParams?: boolean;
  className?: string;
}>;

const Nav = styled.nav<{ expanded: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-self: stretch;
  width: ${({ expanded }) => (expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH)};
  padding: ${NAV_PADDING};
  gap: ${HEADER_GAP};
  background-color: ${theme.colors.backgroundCover.default};
  border-right: 1px solid ${theme.colors.borderSeparator.default};
  transition: width 0.2s ease-in-out;
  overflow: hidden;
`;

const MenuHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${theme.spacing.c};
  flex-direction: ${({ expanded }) => (expanded ? 'row' : 'column')};
  justify-content: ${({ expanded }) => (expanded ? 'space-between' : 'center')};
`;

// The two logos are separate elements toggled with `display`, rather than one element whose `src` changes. Swapping
// `src` leaves the outgoing artwork painted at the incoming width until the new file resolves, which reads as the
// logo stretching; keeping both mounted makes the change a pure style flip. Their aspect ratios are declared here
// instead of being inferred from the files so each box has its final height before the SVG arrives — both are wide
// relative to their height, so they are sized by width to stop the wordmark pushing the toggle out of view.
const Wordmark = styled.img<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  flex-shrink: 0;
  width: ${WORDMARK_WIDTH};
  max-width: 100%;
  aspect-ratio: 126 / 16;
  /* Lines the wordmark up with the item icons below it: the item padding puts the two boxes in the same column,
     and the glyph inset matches their visible edges rather than their bounding boxes. */
  margin-left: calc(${ITEM_PADDING} + ${ICON_GLYPH_INSET});
  /* Aligns the lettering with the toggle glyph instead of the image box - a transform keeps the row height intact,
     which a negative margin would not, since centring absorbs half of it. */
  transform: translateY(${WORDMARK_OPTICAL_LIFT});
`;

const LogoMark = styled.img<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  flex-shrink: 0;
  width: ${LOGO_MARK_WIDTH};
  max-width: 100%;
  aspect-ratio: 32 / 24;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${ITEM_HEIGHT};
  height: ${ITEM_HEIGHT};
  padding: 0;
  border: none;
  border-radius: ${theme.borderRadius.m};
  background-color: transparent;

  &:hover {
    background-color: ${ROW_HIGHLIGHT};
  }

  svg {
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    color: ${theme.colors.iconText.secondary};
  }
`;

const ItemList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.b};
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  list-style: none;

  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.borderMain.default};
    border-radius: ${theme.borderRadius.s};
  }
`;

const ItemLink = styled(Link, { shouldForwardProp: (prop) => prop !== 'active' })<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.e};
  height: ${ITEM_HEIGHT};
  padding: 0 ${ITEM_PADDING};
  border-radius: ${theme.borderRadius.m};
  font-family: ${theme.typography.body.medium.regular.family};
  font-size: ${theme.typography.body.medium.regular.fontSize};
  font-weight: ${({ active }) => (active ? 700 : theme.typography.body.medium.regular.fontWeight)};
  line-height: ${theme.typography.body.medium.regular.lineHeight};
  white-space: nowrap;
  color: ${({ active }) => (active ? theme.colors.text.primary : theme.colors.text.secondary)};
  background-color: ${({ active }) => (active ? ROW_HIGHLIGHT : 'transparent')};

  &:hover,
  &:focus {
    text-decoration: none;
    color: ${theme.colors.text.primary};
    background-color: ${ROW_HIGHLIGHT};
  }

  /* The nav icons are a mix of stroked outlines and filled shapes, so they are coloured through currentColor
     rather than a fill rule, which would flood the outlined ones solid. */
  svg {
    flex-shrink: 0;
    width: ${ICON_SIZE};
    height: ${ICON_SIZE};
    color: ${({ active }) => (active ? theme.colors.brand.primary : theme.colors.iconText.secondary)};
  }
`;

const ItemLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Returns the route of the item matching the current location. When several items match (an index route is a
 * prefix of its siblings), the most specific one wins.
 */
const useActiveRoute = (items: ReportMenuItem[]) => {
  const { pathname } = useLocation();

  return useMemo(
    () =>
      items
        .map((item) => item.route)
        .filter((route) => matchPath(pathname, { path: route }))
        .sort((a, b) => b.length - a.length)[0],
    [items, pathname],
  );
};

export function ReportMenu({ items, preserveQueryParams = false, className }: ReportMenuProps) {
  const { t } = useTranslation('newReport');
  const { search } = useLocation();
  const { expanded, toggleExpanded } = useReportMenuExpanded();
  const activeRoute = useActiveRoute(items);
  const toggleLabel = expanded ? t('menu.collapse') : t('menu.expand');

  return (
    <Nav expanded={expanded} className={className} aria-label={t('menu.ariaLabel')} data-icom="new-report-menu">
      <MenuHeader expanded={expanded}>
        <Wordmark visible={expanded} src="/arnold-logo.svg" alt="Arnold" />
        <LogoMark visible={!expanded} src="/arnold-icon.svg" alt="Arnold" />
        <Tooltip title={toggleLabel} position="right" followCursor={false} delay={TOOLTIP_DELAY_MS}>
          <IconButton type="button" onClick={toggleExpanded} aria-expanded={expanded} aria-label={toggleLabel}>
            {expanded ? <PanelCollapseIcon /> : <PanelExpandIcon />}
          </IconButton>
        </Tooltip>
      </MenuHeader>
      <ItemList>
        {items.map((item) => {
          const active = item.route === activeRoute;
          return (
            <li key={item.route}>
              <Tooltip
                title={item.label}
                position="right"
                disabled={expanded}
                followCursor={false}
                delay={TOOLTIP_DELAY_MS}
              >
                <ItemLink
                  to={preserveQueryParams ? { pathname: item.route, search } : item.route}
                  active={active}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.icon}
                  {expanded && <ItemLabel>{item.label}</ItemLabel>}
                </ItemLink>
              </Tooltip>
            </li>
          );
        })}
      </ItemList>
    </Nav>
  );
}
